import { useEffect, useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import type { UserRole, UserStatus } from '@/types';

export interface UserEditRow {
  uid: string;
  email: string;
  displayName: string;
  companyId: string | null;
  role: UserRole;
  status?: string;
}

type EditableRole = Exclude<UserRole, 'owner'>;

const ROLE_OPTIONS: { value: EditableRole; label: string }[] = [
  { value: 'admin', label: 'Admin (company_admin)' },
  { value: 'recruiter', label: 'Team (reclutador)' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'pending', label: 'Pending' },
];

const STATUS_OPTIONS: { value: UserStatus; label: string }[] = [
  { value: 'active', label: 'Activo' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'disabled', label: 'Deshabilitado' },
];

interface UserEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserEditRow | null;
  companies: { id: string; name: string }[];
  onSaved: () => void;
}

export function UserEditModal({
  open,
  onOpenChange,
  user,
  companies,
  onSaved,
}: UserEditModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [role, setRole] = useState<EditableRole>('viewer');
  const [status, setStatus] = useState<UserStatus>('active');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.displayName ?? '');
    setCompanyId(user.companyId ?? '');
    setRole(
      user.role === 'owner' ? 'admin' : (user.role as EditableRole)
    );
    setStatus((user.status as UserStatus) ?? 'active');
    setError('');
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setError('');
    if (!isOwner && !companyId.trim()) {
      setError('La empresa es obligatoria. El usuario no puede quedar sin empresa.');
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        displayName: displayName.trim() || null,
        status,
        updatedAt: serverTimestamp(),
      };
      if (!isOwner) {
        payload.companyId = companyId.trim() || null;
        payload.role = role;
      }
      await updateDoc(doc(db, 'users', user.uid), payload);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      console.error('Firestore: error actualizando usuario', err);
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar usuario</DialogTitle>
          <DialogDescription>
            {isOwner
              ? 'El rol owner no se puede cambiar desde la UI. Solo nombre y estado.'
              : 'Modifica los datos del usuario. No se puede asignar rol owner desde aquí.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="displayName">Nombre</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Nombre para mostrar"
            />
          </div>
          <div className="space-y-2">
            <Label>Empresa {!isOwner && '*'}</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              disabled={isOwner}
              required={!isOwner}
            >
              {isOwner ? (
                <option value="">—</option>
              ) : (
                <option value="">Selecciona una empresa</option>
              )}
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {isOwner ? (
              <p className="text-xs text-muted-foreground">Owner no tiene empresa asignada.</p>
            ) : (
              <p className="text-xs text-muted-foreground">Obligatorio. Reasignar aquí cambia la empresa del usuario.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Rol</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value as EditableRole)}
              disabled={isOwner}
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {isOwner && (
              <p className="text-xs text-muted-foreground">Rol owner (solo lectura).</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Estado</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as UserStatus)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
